import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Row,
    Col,
    Card,
    ListGroup,
    ListGroupItem,
    Button,
    ButtonGroup,
    Container,
    Label,
    FormGroup,
    Form,
    Modal,
    ModalBody,
    ModalHeader
} from 'reactstrap';

import {
    faExternalLinkAlt,
    faPlus,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import * as Yup from 'yup';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/Auth/AuthContext';

const schema = Yup.object().shape({
    title: Yup.string().required('Title is required!'),
    description: Yup.string().required('Description is required!')
});

export default function MaterialsList(props) {
    const {
        id,
        admin
    } = props;

    const { toggleLoading } = useAuth();

    const [materials, setMaterials] = useState([]);
    const [materialRemove, setMaterialRemove] = useState('');
    const [materialEdit, setMaterialEdit] = useState('');

    const [modalAddMaterial, setModalAddMaterial] = useState(false);
    const toggleModalAddMaterial = useCallback(() => {
        setModalAddMaterial(!modalAddMaterial);
    }, [setModalAddMaterial, modalAddMaterial]);

    const [modalRemoveMaterial, setModalRemoveMaterial] = useState(false);
    const toggleModalRemoveMaterial = useCallback((material = null) => {
        if (material) {
            setMaterialRemove(material);
        }
        setModalRemoveMaterial(!modalRemoveMaterial);
    }, [setModalRemoveMaterial, modalRemoveMaterial, setMaterialRemove]);

    const [modalEditMaterial, setModalEditMaterial] = useState(false);
    const toggleModalEditMaterial = useCallback((material = null) => {
        if (material) {
            setMaterialEdit(material);
        }
        setModalEditMaterial(!modalEditMaterial);
    }, [setModalEditMaterial, modalEditMaterial, setMaterialEdit]);

    const { register, handleSubmit, errors, reset } = useForm({
        validationSchema: schema,
    });

    const getMaterials = useCallback(() => {
        toggleLoading(true);

        api.get(`/ro.team.listMaterialsForGroup/${id.id}`)
            .then((response) => {
                setMaterials(response.data);
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
    }, [toggleLoading, setMaterials, id]);

    const onSubmitAddMaterial = useCallback(
        async (data) => {
            if (!data.file[0]) {
                // Checking the file to upload
                toast.error('You need to select a file');
                return;
            }

            toggleLoading(true);

            let fileReader;

            const handleFileRead = (e) => {

                const fileEncoded64base = btoa(fileReader.result);

                const insertMaterialHeaders = {
                    'title': btoa(data.title),
                    'description': btoa(data.description),
                    'gid': id.id,
                    'originalfilename': data.file[0].name,
                    'Content-Type': data.file[0].type,
                }

                // api.post('/ro.team.insertMaterial', form_data)
                api.put('/ro.team.insertMaterial',
                        fileEncoded64base,
                        {
                            headers: insertMaterialHeaders,
                        }
                    )
                    .then((response) => {
                        toast.success(
                            `Material Added`
                        );
                        toggleModalAddMaterial();
                        reset(response);
                        getMaterials();
                    })
                    .catch((err) => {
                        toast.error(err.response.data.message);
                    })
                    .finally(() =>{
                        toggleLoading(false);
                    });
            };

            fileReader = new FileReader();
            fileReader.onloadend = handleFileRead;
            fileReader.readAsBinaryString(data.file[0]);

        },
        [toggleModalAddMaterial, reset, id.id, getMaterials, toggleLoading]
    );

    const onSubmitRemoveMaterial = useCallback(
        async () => {
            toggleLoading(true);
            api.delete(`/ro.team.removeMaterial/${materialRemove.id}`, {
                data: {
                    gid: id.id
                }
            })
                .then((response) => {
                    toast.success(
                        `Material Removed`
                    );
                    toggleModalRemoveMaterial();
                    getMaterials();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() =>{
                    toggleLoading(false);
                });
        },
        [toggleModalRemoveMaterial, materialRemove, id, getMaterials, toggleLoading]
    );

    const onSubmitEditMaterial = useCallback(
        async (data) => {

            if (!data.file[0]) {
                // Checking the file to upload
                toast.error('You need to select a file');
                return;
            }
            toggleLoading(true);
            let fileReader;

            const handleFileRead = (e) => {
                const fileEncoded64base = btoa(fileReader.result);
                const insertMaterialHeaders = {
                    'title': btoa(data.title),
                    'description': btoa(data.description),
                    'gid': id.id,
                    'originalfilename': data.file[0].name,
                    'Content-Type': data.file[0].type,
                }
                api.put(`/ro.team.modifyMaterial/${materialEdit.id}`,
                        fileEncoded64base,
                        {
                            headers: insertMaterialHeaders,
                        })
                .then((response) => {
                    toast.success(
                        `Material Edited`
                    );
                    toggleModalEditMaterial();
                    reset(response);
                    getMaterials();
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() =>{
                    toggleLoading(false);
                });
            };
            fileReader = new FileReader();
            fileReader.onloadend = handleFileRead;
            fileReader.readAsBinaryString(data.file[0]);
        },
        [toggleModalEditMaterial, reset, id.id, getMaterials, toggleLoading, materialEdit]
    );

    useEffect(() => {
        getMaterials();
    }, [getMaterials]);

    const checkMaterialTitle = () => {
        if (materials.length > 0 || admin) {
            return true;
        }
        return false;
    }
    
    return (
        <Fragment>
            {checkMaterialTitle() && (
                <Fragment>
                    <h5 className="pl-3 pr-3">
                        Group Materials
                    </h5>

                    <div className="divider" />
                </Fragment>
            )}
            
            
            <Container fluid>
                <Row>
                    <Col lg="12">
                        <Card className="main-card mb-3">
                            <ListGroup flush>
                                
                                {materials && materials.map((material) => (
                                    <ListGroupItem key={material.id}>
                                        <div className="widget-content p-0">
                                            <div className="widget-content-wrapper">
                                                <div className="widget-content-left center-elem mr-2">
                                                    <i className="pe-7s-file text-muted fsize-2"/>
                                                </div>
                                                <div className="widget-content-left">
                                                    <div className="widget-heading">
                                                        {material.title}
                                                    </div>
                                                </div>
                                                <div className="widget-content-right">
                                                    {admin && (
                                                        <Fragment>
                                                            <ButtonGroup size="sm" className="mr-3">
                                                                <Button
                                                                className="btn-shadow"
                                                                color="danger"
                                                                onClick={() => toggleModalRemoveMaterial(material)} >
                                                                    <FontAwesomeIcon icon={faTrash} size="1x"/>
                                                                </Button>
                                                                <Button
                                                                className="btn-shadow"
                                                                color="info"
                                                                onClick={() => toggleModalEditMaterial(material)} >
                                                                    Edit
                                                                </Button>
                                                            </ButtonGroup>
                                                        </Fragment>
                                                    )}
                                                    <a
                                                    href={material.url}
                                                    target="_blank"
                                                    className="btn-icon btn-icon-only"
                                                    color="link"
                                                    rel="noopener noreferrer" >
                                                        <FontAwesomeIcon icon={faExternalLinkAlt}/>
                                                    </a>                                                    
                                                </div>
                                            </div>
                                        </div>
                                    </ListGroupItem>
                                ))}
                                
                            </ListGroup>
                        </Card>
                        
                    </Col>
                    
                </Row>
            </Container>

            <Container fluid>

                {admin && (
                    <Row>

                        <Col lg="12">
                            <Button 
                            className="mb-2 mr-2 btn-icon btn-icon-only" 
                            color="primary" 
                            onClick={toggleModalAddMaterial} >
                                <FontAwesomeIcon icon={faPlus} size="1x"/> Add Material
                            </Button>
                        </Col>

                        <Modal isOpen={modalAddMaterial} toggle={toggleModalAddMaterial}>
                            <ModalHeader toggle={toggleModalAddMaterial}>
                                Add Material
                            </ModalHeader>
                            <ModalBody>
                                <Form>
                                    <FormGroup row>
                                        <Label for="title" sm={2}>
                                            Title
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="text"
                                            name="title"
                                            id="title"
                                            className="form-control"
                                            ref={register} />
                                            {errors.title && (
                                                <p className="error-validation-left">
                                                    {
                                                        errors.title.message
                                                    }
                                                </p>
                                            )}
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label for="description" sm={2}>
                                            Description
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="text"
                                            name="description"
                                            id="description"
                                            className="form-control"
                                            ref={register} />
                                            {errors.description && (
                                                <p className="error-validation-left">
                                                    {
                                                        errors.description.message
                                                    }
                                                </p>
                                            )}
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label for="file" sm={2}>
                                            File
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="file"
                                            name="file"
                                            id="file"
                                            className="form-control"
                                            ref={register} />
                                        </Col>
                                    </FormGroup>
                                    <div className="text-center">
                                        <Button 
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="info"
                                        onClick={handleSubmit(onSubmitAddMaterial)} >
                                            OK
                                        </Button>

                                        <Button 
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="secondary"
                                        onClick={toggleModalAddMaterial} >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </ModalBody>
                        </Modal>

                        <Modal isOpen={modalRemoveMaterial} toggle={toggleModalRemoveMaterial}>
                            <ModalHeader toggle={toggleModalRemoveMaterial}>
                                Remove Material
                            </ModalHeader>
                            <ModalBody>
                                <Form>
                                    <FormGroup row>
                                        <Label for="title" sm={2}>
                                            Title
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="text"
                                            name="title"
                                            id="title"
                                            className="form-control"
                                            defaultValue={materialRemove.title}
                                            disabled={true} />
                                        </Col>
                                    </FormGroup>
                                    <div className="text-center">
                                        <Button
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="danger"
                                        onClick={onSubmitRemoveMaterial} >
                                            Remove
                                        </Button>

                                        <Button 
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="secondary"
                                        onClick={toggleModalRemoveMaterial} >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </ModalBody>
                        </Modal>

                        <Modal isOpen={modalEditMaterial} toggle={toggleModalEditMaterial}>
                            <ModalHeader toggle={toggleModalEditMaterial}>
                                Edit Material
                            </ModalHeader>
                            <ModalBody>
                                <Form>
                                    <FormGroup row>
                                        <Label for="title" sm={2}>
                                            Title
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="text"
                                            name="title"
                                            id="title"
                                            className="form-control"
                                            ref={register}
                                            defaultValue={materialEdit.title} />
                                            {errors.title && (
                                                <p className="error-validation-left">
                                                    {
                                                        errors.title.message
                                                    }
                                                </p>
                                            )}
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label for="description" sm={2}>
                                            Description
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="text"
                                            name="description"
                                            id="description"
                                            className="form-control"
                                            ref={register}
                                            defaultValue={materialEdit.description} />
                                            {errors.description && (
                                                <p className="error-validation-left">
                                                    {
                                                        errors.description.message
                                                    }
                                                </p>
                                            )}
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label for="file" sm={2}>
                                            File
                                        </Label>
                                        <Col sm={10}>
                                            <input
                                            type="file"
                                            name="file"
                                            id="file"
                                            className="form-control"
                                            ref={register} />
                                        </Col>
                                    </FormGroup>
                                    <div className="text-center">
                                        <Button
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="info"
                                        onClick={handleSubmit(onSubmitEditMaterial)} >
                                            OK
                                        </Button>

                                        <Button 
                                        className="btn-wide mb-2 mr-2" 
                                        size="md" 
                                        color="secondary"
                                        onClick={toggleModalEditMaterial} >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </ModalBody>
                        </Modal>

                    </Row>
                )}

            </Container>
        </Fragment>
    );
    
};